<template>
  <b-row class="section">
    <div class="mc-point-wrapper">
      <div class="mb-3">
        <h5>{{ $t('fleetTaskInstant.title.route') }}</h5>
      </div>
      <div class="trip-wrapper">
        <div v-if="listPointRes.points?.length > 1" class="point-info-wrapper">
          <span>
            {{ $t('global.text.total') }}: <span>{{ listPointRes.points?.length }}</span> {{ $t('global.text.destination') }}
          </span>
          <template v-if="listPointRes.total_distance != 0">
            <span class="__divider" />
            <span v-html="formatDistance(listPointRes.total_distance)" />
          </template>
          <template v-if="listPointRes.total_duration != 'PT0S'">
            <span class="__divider" />
            <span v-html="formatDuration(listPointRes.total_duration)" />
          </template>
        </div>
        <div class="late-notification-wrapper">
          <McToggle
            v-model="formValues.send_late_notification"
            class="d-flex"
            :disabled="isLoadingGlobal"
            @change="handleChangeInput($event)"
          />
          <span>{{ formValues.send_late_notification ? $t('fleetTaskInstant.action.lateNotification.active') : $t('fleetTaskInstant.action.lateNotification.inactive') }}</span>
          <Tooltip :name="formValues.send_late_notification ? 'late-notification-active-state' : 'late-notification-not-active-state'" />
        </div>
      </div>
      <div class="table-responsive border mc-point-table">
        <b-table-simple class="editable-table" sticky-header>
          <b-thead>
            <b-tr>
              <b-th v-for="field in pointFields" :key="`th-${field.key}`" :style="field.thStyle">
                {{ field.label }}
              </b-th>
              <b-th v-if="points.length > 1" />
            </b-tr>
          </b-thead>
          <!-- draggable component -->
          <draggable
            v-model="points"
            tag="tbody"
            handle=".drag-handle"
            :move="checkMove"
            @change="handleDragPoint"
            @start="dragging = true"
            @end="dragging = false"
          >
            <b-tr v-for="(item, index) in points" :key="index">
              <template v-for="field in pointFields">
                <!-- Draggable Handle and Index Display -->
                <b-td
                  v-if="field.key === 'drag'"
                  :key="`${field.key}-${item.id}`"
                  class="mc-order-wrapper"
                  :class="{
                    'drag-handle': isDraggable(index) && !isLoadingGlobal && !isAdding,
                  }"
                >
                  <div
                    class="mc-drag-wrapper"
                    :class="{
                      'no-drag': !isDraggable(index) || isLoadingGlobal || isAdding,
                      drag: isDraggable(index) && !isLoadingGlobal && !isAdding,
                    }"
                  >
                    <i
                      v-if="isDraggable(index) && !isLoadingGlobal && !isAdding"
                      class="mc-drag icon-outline_move"
                    ></i>
                    <div class="mc-badge-wrapper">
                      <span class="mc-label-badge">
                        <i
                          v-if="points[index].type === START_POINT_TYPE"
                          class="mc-icon icon-fill_home"
                        />
                        <i
                          v-else-if="points[index].type === END_POINT_TYPE"
                          class="mc-icon icon-fill_flag"
                        />
                        <span v-else-if="points[0]?.type === START_POINT_TYPE">{{ index }}</span>
                        <span v-else>{{ index + 1 }}</span>
                      </span>
                    </div>
                  </div>
                </b-td>

                <b-td v-else-if="field.key == 'address'" :key="`td-${field.key}`">
                  <AddressInput
                    fixed
                    :placeholder="$t('fleetTaskInstant.placeholder.searchDestination')"
                    :modelValue="
                      item.address
                        ? { ...item.address, id: item.id || Date.now() }
                        : { id: undefined }
                    "
                    :classes="{
                      'point-form-error-input':
                        formErrors[`points.${index}.${field.key}`] != undefined,
                    }"
                    :disabled="isLoadingGlobal || (isAdding && newPointIndex !== index)"
                    @update:modelValue="(value) => updateAddress({ index, value })"
                  />
                  <div
                    v-if="formErrors[`points.${index}.${field.key}`] != undefined"
                    :key="`errors-${field.key}`"
                    class="point-form-error"
                  >
                    {{ formErrors[`points.${index}.${field.key}`] }}
                  </div>

                  <div
                    v-if="
                      ((index === 0 && points.length - (newPointIndex ? 1 : 0) > 1) ||
                        index === points.length - 1) &&
                      points.length > 1
                    "
                    class="d-flex align-items-center gap-1"
                  >
                    <FormulateInput
                      :key="refreshCheckbox"
                      class="mc-inline-checkbox"
                      type="checkbox"
                      :checked="item['type'] != DEFAULT_POINT_TYPE"
                      :disabled="isLoadingGlobal || (isAdding && newPointIndex !== index)"
                      :label="
                        index === 0 ? $t('fleetTaskInstant.action.point.start') : $t('fleetTaskInstant.action.point.end')
                      "
                      @input="
                        updateType(index, $event, index == 0 ? START_POINT_TYPE : END_POINT_TYPE)
                      "
                    />
                    <Tooltip :name="index === 0 ? 'start-point' : 'end-point'" />
                  </div>
                </b-td>

                <!-- Service Time Field -->
                <b-td v-else-if="field.key == 'service_time'" :key="`td-${field.key}`">
                  <ServiceTimeInput
                    :ref="`${field.key}-ref`"
                    fixed
                    :field="field"
                    :item="item"
                    :initialValues="item[field.key]"
                    :disabled="
                      isTypeNotEmpty(index, field.key) ||
                      isLoadingGlobal ||
                      (isAdding && newPointIndex !== index)
                    "
                    @input="handleInputChange([field.key], index)"
                  />
                </b-td>

                <!-- Action Buttons (e.g., Pickup, Drop) -->
                <b-td v-else-if="field.key == 'activity_label'" :key="`td-${field.key}`">
                  <div class="d-flex justify-content-around">
                    <b-button
                      pill
                      variant="secondary"
                      :class="{ selected: isSelected(index, PICKUP_ACTIVITY_TYPE) }"
                      :disabled="
                        isTypeNotEmpty(index, field.key) ||
                        isLoadingGlobal ||
                        (isAdding && newPointIndex !== index)
                      "
                      @click="selectValue(index, PICKUP_ACTIVITY_TYPE)"
                    >
                      <i class="mc-fa icon-outline_arrow-up" />{{
                        $t('fleetTaskInstant.text.pickup')
                      }}
                    </b-button>
                    <b-button
                      pill
                      variant="secondary"
                      :class="{ selected: isSelected(index, DROP_ACTIVITY_TYPE) }"
                      :disabled="
                        isTypeNotEmpty(index, field.key) ||
                        isLoadingGlobal ||
                        (isAdding && newPointIndex !== index)
                      "
                      @click="selectValue(index, DROP_ACTIVITY_TYPE)"
                    >
                      <i class="mc-fa icon-outline_arrow-down" />{{
                        $t('fleetTaskInstant.text.drop')
                      }}
                    </b-button>
                  </div>
                </b-td>

                <!-- Input Field for Other Fields -->
                <b-td v-else :key="`td-${field.key}`">
                  <FormulateInput
                    :key="field.key"
                    :ref="`${field.key}-ref`"
                    v-model="item[field.key]"
                    v-bind="field"
                    :rowItem="item"
                    :label="null"
                    :name="field.key"
                    :type="field.type || 'text'"
                    :validation-name="field.label"
                    :disabled="
                      isTypeNotEmpty(index, field.key) ||
                      (field.name !== 'note' && isLoadingGlobal) ||
                      (isAdding && newPointIndex !== index)
                    "
                    validation-behavior="live"
                    error-behavior="value"
                    size="sm"
                    class="p-0"
                    :error="formErrors[`points.${index}.${field.key}`]"
                    @input="handleInputChange([field.key], index)"
                  />
                </b-td>
              </template>

              <!-- Delete Button -->
              <b-td v-if="points.length > 1">
                <b-button
                  v-if="points.length - (newPointIndex ? 1 : 0) > 1 || item?.address === null"
                  variant="outline"
                  class="__delete-btn"
                  :disabled="isLoadingGlobal || (isAdding && newPointIndex !== index)"
                  @click="removePoint(index)"
                >
                  <i class="mc-icon icon-outline_trash" />
                </b-button>
              </b-td>
            </b-tr>
          </draggable>
        </b-table-simple>
      </div>
    </div>
    <div class="d-flex my-4 gap-3">
      <McButton
        block
        variant="primary"
        :text="$t('fleetTaskInstant.action.addDestination')"
        icon="icon-fill_plus"
        style="max-width: 272px"
        :disabled="isLoadingGlobal || isAdding"
        @click="addPoint"
      />
      <McButton
        block
        variant="secondary"
        :text="$t('fleetTaskInstant.action.optimizeRoute')"
        icon="icon-fill_flash"
        style="max-width: 272px"
        :disabled="isLoadingGlobal || isAdding || hasTooFewTypeDefault"
        @click="showOptimizeRouteDialog"
      />
      <McButton
        block
        variant="secondary"
        :text="$t('fleetTaskInstant.action.calculateSchedule')"
        icon="icon-outline_calculate"
        style="max-width: 272px"
        :disabled="isLoadingGlobal || isAdding || points.length < 2"
        @click="showCalculateScheduleDialog"
      />
    </div>
    <b-modal
      id="calculate-schedule-dialog"
      centered
      class="mc-modal"
      :title="$t('global.confirmation.title')"
      :ok-title="$t('fleetTaskInstant.action.continueCalculate')"
      :cancel-title="$t('global.action.cancel')"
      @ok="showCalculateScheduleProgressDialog"
    >
      <div>
        {{ $t('fleetTaskInstant.text.calculateSchedule') }}
      </div>
      <div>{{ $t('fleetTaskInstant.text.askToContinue') }}</div>
      <div class="mt-3">
        <b-form-checkbox
          v-model="dontShowCalculateScheduleConfirmationCheckbox"
          class="mb-1 mt-1"
          plain
        >
          {{ $t('fleetTaskInstant.action.doNotShowAgain') }}
        </b-form-checkbox>
      </div>
    </b-modal>
    <b-modal
      id="optimize-dialog"
      centered
      class="mc-modal"
      :title="$t('global.confirmation.title')"
      :ok-title="$t('fleetTaskInstant.action.continueOptimize')"
      :cancel-title="$t('global.action.cancel')"
      @ok="showProgressBarPercentageDialog"
    >
      <div>
        {{ $t('fleetTaskInstant.text.calculateOptimize') }}
      </div>
      <div>{{ $t('fleetTaskInstant.text.askToContinue') }}</div>
      <div class="mt-3">
        <b-form-checkbox
          v-model="dontShowOptimizeRouteConfirmationCheckbox"
          class="mb-1 mt-1"
          plain
        >
          {{ $t('fleetTaskInstant.action.doNotShowAgain') }}
        </b-form-checkbox>
      </div>
    </b-modal>
    <b-modal
      id="optimize-progress"
      :no-close-on-backdrop="true"
      hide-header
      hide-footer
      centered
      modal-class="progress-modal"
      @show="optimizeProgressModalOpened"
    >
      <div class="px-4 mb-3">
        <p class="text-center caption-heading">
          {{ $t('fleetTaskInstant.text.optimizeProgress.title') }}
        </p>
        <p class="text-center caption">
          {{ $t('fleetTaskInstant.text.optimizeProgress.caption') }}
        </p>
      </div>
      <b-progress :value="progressBarPercentage" :max="100" />
    </b-modal>
    <b-modal
      id="calculate-schedule-progress"
      :no-close-on-backdrop="true"
      hide-header
      hide-footer
      centered
      modal-class="progress-modal"
      @show="calculateScheduleProgressModalOpened"
    >
      <div class="px-4 mb-3">
        <p class="text-center caption-heading">
          {{ $t('fleetTaskInstant.text.calculateScheduleProgress.title') }}
        </p>
        <p class="text-center caption">
          {{ $t('fleetTaskInstant.text.calculateScheduleProgress.caption') }}
        </p>
      </div>
      <b-progress :value="progressBarPercentage" :max="100" />
    </b-modal>
  </b-row>
</template>
<script>
import { mapState } from 'vuex'
import draggable from 'vuedraggable'
import cloneDeep from 'lodash/cloneDeep'
import * as FleetTaskInstant from '@/services/rest-api/lib/fleet-task-instant/fleet-task-instant'

import McButton from '@/components/McButton'
import McToggle from '@/components/McToggle'
import Tooltip from '../Tooltip'
import pointFields from '../../lib/fields/point-fields'
import AddressInput from '../../wrapper/AddressInput'
import ServiceTimeInput from '../../wrapper/ServiceTimeInput'

import { EventBus } from '../../lib/event-bus.js'
import { formatNumber } from '@/helpers/format-number'

import { FORM_CREATE_POINTS_DEFAULT } from '../../lib/constants/default-fields'
import {
  DEFAULT_POINT_TYPE,
  END_POINT_TYPE,
  START_POINT_TYPE,
  PICKUP_ACTIVITY_TYPE,
  DROP_ACTIVITY_TYPE,
  GOOGLE_MAP_ADDRESS_TYPE,
  MASTER_DATA_ADDRESS_TYPE,
} from '../../lib/constants/types'
import errorInputMsg from '../../lib/constants/error-input-msg.js'
import { successMsg, errorMsg } from '../../lib/constants/toast-msg.js'

import moment from 'moment'
moment.locale('id')

export default {
  components: {
    draggable,
    AddressInput,
    ServiceTimeInput,
    McButton,
    McToggle,
    Tooltip,
  },
  mixins: [pointFields],
  data() {
    return {
      START_POINT_TYPE,
      END_POINT_TYPE,
      DEFAULT_POINT_TYPE,
      PICKUP_ACTIVITY_TYPE,
      DROP_ACTIVITY_TYPE,
      GOOGLE_MAP_ADDRESS_TYPE,
      MASTER_DATA_ADDRESS_TYPE,
      FAKE_LOADING_SMOOTHNESS: 30,

      isRemovingPoint: false,
      dragging: false,
      newPointIndex: 0,
      points: [cloneDeep(FORM_CREATE_POINTS_DEFAULT)],
      errors: {},
      formErrors: {},
      refreshCheckbox: 1,
      progressBarPercentage: 0,
      optimizeRouteResponse: null,
      progressBarApiLoading: false,
      showOptimizeRouteConfirmation: true,
      showCalculateScheduleConfirmation: true,
      calculateScheduleResponse: null,
      dontShowOptimizeRouteConfirmationCheckbox: false,
      dontShowCalculateScheduleConfirmationCheckbox: false,

      formValues: {
        send_late_notification: true,
      },
    }
  },
  computed: {
    ...mapState('fleetTaskInstant', ['isLoadingGlobal', 'errorResponse', 'listPointRes', 'showResponse']),
    isAdding() {
      return this.newPointIndex != null ? this.points[this.newPointIndex]?.address === null : false
    },
    /**
     * Checks if there are too few non-type rows to allow dragging by checking the value of type properties on points.
     * This computed to prevent the condition of normal point that below the requirement, the requirement is 2 or more for normal point
     * *normal point: point that not set as start point or end point.
     *
     * @returns {boolean} - True if there are too few non-type rows, false otherwise.
     */
    hasTooFewTypeDefault() {
      var nonTypeItems = 0
      for (let item of this.points) {
        if (item.type !== DEFAULT_POINT_TYPE || item.address === null) {
          nonTypeItems++
        }
      }
      return this.points.length - nonTypeItems < 2
    },
  },
  watch: {
    errorResponse(newValue) {
      if (Object.keys(newValue).length === 0) {
        this.formErrors = {}
        return
      }

      if (newValue.points === undefined) {
        Object.keys(newValue).forEach((key) => {
          if (!key.includes('points')) return
          let message

          if (newValue[key][0].includes('Point geolocation overlap with point')) {
            message = errorInputMsg['address/point-overlap']()
          } else if (newValue[key][0].includes('value not found')) {
            message = errorInputMsg['address/value-not-found']()
          } else {
            message = errorInputMsg[`${key}/${newValue[key][0]}`]

            if (typeof message === 'function') {
              message = message()
            }
          }

          this.$set(this.formErrors, key, message)
        })

        return
      }

      newValue.points.forEach((point, index) => {
        for (const [key, error] of Object.entries(point)) {
          let message = errorInputMsg[`${key}/${error[0]}`]
          if (typeof message === 'function') {
            message = message()
          }

          let errorKey = `points.${index}.${key}`

          this.$set(this.formErrors, errorKey, message)
        }
      })
    },
    showResponse: {
      handler(newValue) {
        this.$set(this.formValues, 'send_late_notification', newValue.send_late_notification)
      },
      immediate: true,
    }
  },
  created() {
    if (this.$route.params.id === undefined) return

    const showOptimizeRouteConfirmationData = localStorage.getItem('showOptimizeRouteConfirmation')
    const showCalculateScheduleConfirmationData = localStorage.getItem(
      'showCalculateScheduleConfirmation'
    )
    this.showOptimizeRouteConfirmation = [null, '', 'true'].includes(
      showOptimizeRouteConfirmationData
    )
    this.showCalculateScheduleConfirmation = [null, '', 'true'].includes(
      showCalculateScheduleConfirmationData
    )
    this.getPointsData()

    EventBus.$on('remove-point', this.removePoint)
    EventBus.$on('add-new-point', async (value) => {
      await this.addPoint()

      let index = this.points.length - 1
      if (this.points[this.points.length - 1].type !== DEFAULT_POINT_TYPE) {
        index -= 1
      }

      this.updateAddress({ index, value })
    })
  },
  beforeDestroy() {
    EventBus.$off('remove-point')
    EventBus.$off('add-new-point')
  },
  methods: {
    formatDuration(durationISO8601) {
      const duration = moment.duration(durationISO8601)

      const days = duration.days()
      const hours = duration.hours()
      const minutes = duration.minutes()

      const formattedDuration = []

      if (days > 0) {
        const unit = this.$t('fleetTaskInstant.time.day')

        formattedDuration.push(`<span>${days}</span> ${unit}`)
      }

      if (hours > 0) {
        const unit = this.$t('fleetTaskInstant.time.hour')

        formattedDuration.push(`<span>${hours}</span> ${unit}`)
      }

      if (minutes > 0) {
        const unit = this.$t('fleetTaskInstant.time.minute')

        formattedDuration.push(`<span>${minutes}</span> ${unit}`)
      }

      return formattedDuration.join(' ')
    },

    formatDistance(value) {
      let distance = value
      if (distance >= 1000) {
        return `<span>${formatNumber(distance / 1000)}</span> km`
      }
      return '<span>< 1</span> km'
    },
    /**
     * Get list of points
     */
    async getPointsData(type = 'init') {
      if (this.isLoadingGlobal) return
      this.$store.dispatch('fleetTaskInstant/setIsLoadingGlobal', true)

      const res = await FleetTaskInstant.getPoints(this.$route.params.id)

      if (res.errors) {
        this.errors = this.mapErrorsV2(res.errors)
        this.$store.dispatch('fleetTaskInstant/setErrorResponse', this.errors)
      }

      this.$store.dispatch('fleetTaskInstant/setListPointRes', cloneDeep(res.data))
      EventBus.$emit('update-map')

      if (type === 'init') {
        this.parseResponse(res.data.points)
      }

      this.$nextTick(async () => {
        await this.$store.dispatch('fleetTaskInstant/setIsLoadingGlobalDelay', false)
      })
    },

    mapErrorsV2(errors_v2) {
      const mappedErrors = {}

      if (errors_v2.issues && errors_v2.issues.length > 0) {
        errors_v2.issues.forEach((issue) => {
          let key
          if (issue.path.includes('address')) {
            key = issue.path.slice(0, -1).join('.')
          } else {
            key = issue.path.join('.')
          }

          mappedErrors[key] = [issue.message]
        })
      }

      return mappedErrors
    },

    parseResponse(data) {
      if (data.length === 0) return
      const points = []

      data.forEach((item) => {
        points.push({
          id: item.id,
          address: item.address,
          target_time_arrival: item.target_time_arrival,
          service_time: item.service_time,
          target_time_departure: item.target_time_departure,
          activity_label: item.activity_label.raw_type,
          note: item.note,
          type: item.type,
        })
      })

      this.points = points
      this.newPointIndex = null
    },
    /**
     * Determines if a row is draggable or not.
     *
     * @param {number} index - The index of the row.
     * @returns {boolean} - True if the row is draggable, false otherwise.
     */
    isDraggable(index) {
      return (
        !this.hasTooFewTypeDefault &&
        this.points[index].type === DEFAULT_POINT_TYPE &&
        this.points.length > 1 &&
        this.points[index].address !== null
      )
    },
    updateAddress({ index, value }) {
      // if updated address has error bcs overlapping geofence,
      // delete address error message that related to
      this.removeRelatedPointError(index)

      this.removeFormError(`points.${index}.address`)
      this.newPointIndex = null
      this.$set(this.points[index], 'address', cloneDeep(value))
      
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => {
        this.handleSavePoint(['address'], index)
      }, 500)
    },
    /**
     * Checks if the 'type' property of a row is not empty, based on the row index and the field key.
     * This function is used for disabled the intended input for specific row, like:
     *    1. if the start type is checked, the target_time_departure, service_time, and activity_label must be disabled
     *    2. if the end type is checked, the target_time_arrival, service_time, and activity_label must be disabled
     *
     * @param {number} index - The index of the row.
     * @param {string} key - The field key to check.
     * @returns {boolean} - True if the 'type' property is not empty.
     */
    isTypeNotEmpty(index, key) {
      return (
        (index === 0 || index === this.points.length - 1) &&
        ((key === 'target_time_departure' && this.points[index]['type'] === END_POINT_TYPE) ||
          (key === 'target_time_arrival' && this.points[index]['type'] === START_POINT_TYPE) ||
          (this.points[index]['type'] !== DEFAULT_POINT_TYPE && key === 'service_time'))
      )
    },
    /**
     * Updates the 'type' checkbox property of a row.
     * value: ['start','end','default']
     *
     * @param {number} index - The index of the row to update.
     * @param {boolean} isChecked - Indicates whether the 'type' property should be set or cleared.
     * @param {string} type - The type value to set for the 'type' property, the 3 value defined.
     */
    updateType(index, isChecked, type) {
      this.$nextTick(() => {
        var keys = ['type']
        if (isChecked) {
          this.$set(this.points[index], 'type', type)
        } else {
          this.$set(this.points[index], 'type', DEFAULT_POINT_TYPE)

          // for swapping between last index type end with new entry point above the last index
          if (index === this.points.length - 1 && type === END_POINT_TYPE) {
            const secondLastIndex = this.points.length - 2
            if (secondLastIndex >= 0 && !this.points[secondLastIndex].address) {
              // Swap the last two points
              [this.points[secondLastIndex], this.points[index]] = [
                this.points[index],
                this.points[secondLastIndex],
              ]
              index = secondLastIndex
              this.newPointIndex = this.points.length - 1
            }
          }
        }
        this.handleSavePoint(keys, index)
      })
    },
    handleInputChange: function (keys, index) {
      if (this.dragging || this.isRemovingPoint) return
      if (this.isLoadingGlobal && !keys.includes('note')) return
      
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => {
        const errorsKey = `points.${index}.${keys}`
        if (this.formErrors[errorsKey] !== undefined) {
          this.removeFormError(errorsKey)
        }

        this.handleSavePoint(keys, index)
      }, 500)
    },
    /**
     * Checks if the value of the 'activity_label' buttons for the row at the given index matches the provided value.
     * Value: Pickup, Drop
     *
     * @param {number} index - The index of the row to check.
     * @param {string} value - The value to compare the 'activity_label' field against.
     * @returns {boolean} - True if the 'activity_label' field matches the provided value, false otherwise.
     */
    isSelected(index, value) {
      return this.points[index]?.activity_label === value
    },
    /**
     * Sets the 'activity_label' buttons of the form values at the given index to the provided value.
     * Value: Pickup, Drop
     *
     * @param {number} index - The index of the form values to update.
     * @param {string} value - The new value to set for the 'activity_label' field.
     */
    selectValue(index, value) {
      if (this.points[index].activity_label !== value) {
        this.$set(this.points[index], 'activity_label', value)
      } else {
        this.$set(this.points[index], 'activity_label', null)
      }
      this.handleSavePoint(['activity_label'], index)
    },
    /**
     * Adds a new points object to the `formValues.points` array.
     *
     * If the last point in the array has its `type` property set, the new point will be inserted before the last one.
     * Otherwise, the new point will be appended to the end of the array.
     *
     * The new point object is created by cloning the `FORM_CREATE_POINTS_DEFAULTS` object.
     */
    async addPoint() {
      const newPoint = cloneDeep(FORM_CREATE_POINTS_DEFAULT)

      let lastIndex = this.points.length - 1
      if (this.points[lastIndex].type !== DEFAULT_POINT_TYPE) {
        this.points.splice(this.points.length - 1, 0, newPoint)
        this.newPointIndex = this.points.length - 2
      } else {
        this.points.push(newPoint)
        this.newPointIndex = this.points.length - 1
      }

      await this.$nextTick()
    },
    /**
     * Removes a row from the point table.
     *
     * @param {number} index - The index of the row to remove.
     * @returns {void}
     */
    removePoint(index) {
      if (
        this.points.length - (this.newPointIndex ? 1 : 0) > 1 ||
        this.points[index].address === null
      ) {
        this.isRemovingPoint = true
        const removedPoint = this.points.splice(index, 1)[0]

        if (removedPoint?.id != null) {
          if (removedPoint.type == START_POINT_TYPE) {
            this.refreshCheckbox += 1
          }

          this.handleDeletePoint(removedPoint)
          this.removeRelatedPointError(index)
        } else {
          this.newPointIndex = null
        }
        this.$nextTick(() => {
          this.isRemovingPoint = false
        })
      }
    },
    /**
     * Checks if a row can be moved during a drag and drop operation to the target drop.
     *
     * @param {Object} event - The drag and drop event object.
     * @returns {boolean} - True if the row can be moved to target drop, false otherwise.
     */
    checkMove(event) {
      const { relatedContext } = event
      const targetIndex = relatedContext.index

      if (
        this.points[targetIndex].type !== DEFAULT_POINT_TYPE ||
        !this.points[targetIndex].address
      ) {
        return false
      }

      return true
    },
    /**
     * Removes error by error key from form error.
     *
     * @param {string} errorKey - Key for the error.
     * @returns {void}
     */
    removeFormError(errorKey) {
      this.$delete(this.formErrors, errorKey)
    },
    removeRelatedPointError(index) {
      // check if the updated point has error overlapping
      if (
        this.errorResponse?.[`points.${index}.address`]?.[0].includes(
          'Point geolocation overlap with point'
        ) === undefined
      )
        return

      // get the related point index
      const splitted = this.errorResponse[`points.${index}.address`][0].split(' ')

      // remove the error message
      this.removeFormError(`points.${splitted[7]}.address`)
    },
    handleDragPoint({ moved: event }) {
      this.handleSavePoint(['plan_sequence'], event.newIndex)
      this.$store.dispatch('fleetTaskInstant/setErrorResponse', {})
    },
    async handleSavePoint(keys, index) {
      if (this.isLoadingGlobal && !keys.includes('note')) {
        return
      }

      const point = cloneDeep(this.points[index])

      if (keys.includes('address')) {
        var address = {}
        switch (point.address.type) {
          case MASTER_DATA_ADDRESS_TYPE:
            address = {
              type: MASTER_DATA_ADDRESS_TYPE,
              id: `${point.address.id}`,
            }
            break
          case GOOGLE_MAP_ADDRESS_TYPE:
            address = {
              type: GOOGLE_MAP_ADDRESS_TYPE,
              id: null, // change it when the place_id is used on the address input
              name: point.address.name,
              full_name: point.address.full_name || point.address.fullName,
              lat: point.address.geolocation?.coordinate?.latitude || point.address.lat,
              lng: point.address.geolocation?.coordinate?.longitude || point.address.lng,
            }
            break
        }
        point.address = address
      }

      if (point?.id) {
        this.$store.dispatch('fleetTaskInstant/setIsInitial', false)
        await this.$store.dispatch('fleetTaskInstant/setIsLoadingGlobalDelay', true)
        if (keys.includes('plan_sequence')) {
          point['plan_sequence'] = index + 1
        }

        // to update the change input only
        var formData = {}
        keys.forEach((key) => {
          formData[key] = point[key]
        })

        const res = await FleetTaskInstant.updatePoint(this.$route.params.id, point.id, formData)

        if (res.errors) {
          this.$mcToast.error(errorMsg['default']())
          await this.$store.dispatch('fleetTaskInstant/setIsLoadingGlobalDelay', false)

          return
        }

        this.$store.dispatch('fleetTaskInstant/setIsLoadingGlobal', false)
        this.getPointsData(keys.includes('note') ? 'update-point' : 'init')
      } else {
        // to handle only create point when address is inputed
        if (!point?.address) {
          await this.$store.dispatch('fleetTaskInstant/setIsLoadingGlobalDelay', false)
          return
        }

        this.$store.dispatch('fleetTaskInstant/setIsInitial', false)
        await this.$store.dispatch('fleetTaskInstant/setIsLoadingGlobalDelay', true)

        const formData = {
          ...point,
          service_time: point.type === DEFAULT_POINT_TYPE ? point.service_time : null,
          target_time_arrival: point.type !== START_POINT_TYPE ? point.target_time_arrival : null,
          target_time_departure: point.type !== END_POINT_TYPE ? point.target_time_departure : null,
          activity_label: point.type === DEFAULT_POINT_TYPE ? point.activity_label : null,
        }

        const res = await FleetTaskInstant.storePoint(this.$route.params.id, formData)

        if (res.errors) {
          this.$mcToast.error(errorMsg['default']())
          return
        }

        this.$store.dispatch('fleetTaskInstant/setIsLoadingGlobal', false)
        this.getPointsData()
      }

      EventBus.$emit('refetch-fleet-task-instant')
    },
    async handleDeletePoint(point) {
      if (this.isLoadingGlobal) return

      if (point?.id) {
        this.$store.dispatch('fleetTaskInstant/setIsInitial', false)
        await this.$store.dispatch('fleetTaskInstant/setIsLoadingGlobalDelay', true)

        const res = await FleetTaskInstant.deletePoint(this.$route.params.id, point.id)

        if (res.errors) {
          this.$mcToast.error(errorMsg['default']())
          await this.$store.dispatch('fleetTaskInstant/setIsLoadingGlobalDelay', false)
          return
        }

        this.$store.dispatch('fleetTaskInstant/setIsLoadingGlobal', false)
        this.getPointsData()
        EventBus.$emit('refetch-fleet-task-instant')
      }
    },
    showProgressBarPercentageDialog() {
      if (this.showOptimizeRouteConfirmation && this.dontShowOptimizeRouteConfirmationCheckbox) {
        localStorage.setItem('showOptimizeRouteConfirmation', 'false')
      }

      this.$bvModal.show('optimize-progress')
    },
    showOptimizeRouteDialog() {
      if (this.points.length < 3) {
        this.$mcToast.error(errorMsg['required-more-point']())
        return
      }
      const nullAddressIndices = []
      this.points.forEach((point, index) => {
        if (!point.id) {
          nullAddressIndices.push(index)
        }
      })
      if (nullAddressIndices.length > 0) {
        nullAddressIndices.forEach((index) => {
          this.$set(this.formErrors, `points.${index}.address`, errorInputMsg['address/required']())
        })
        return
      }

      if (!this.showOptimizeRouteConfirmation) {
        this.showProgressBarPercentageDialog()
        return
      }

      this.$bvModal.show('optimize-dialog')
    },
    showCalculateScheduleProgressDialog() {
      if (
        this.showCalculateScheduleConfirmation &&
        this.dontShowCalculateScheduleConfirmationCheckbox
      ) {
        localStorage.setItem('showCalculateScheduleConfirmation', 'false')
        this.showCalculateScheduleConfirmation = false
      }

      this.$bvModal.show('calculate-schedule-progress')
    },
    showCalculateScheduleDialog() {
      const firstPoint = this.points[0]
      if (firstPoint.type !== 'START' && firstPoint.target_time_arrival === null) {
        this.$set(
          this.formErrors,
          'points.0.target_time_arrival',
          errorInputMsg['target_time_arrival/required']()
        )

        this.$mcToast.error(errorMsg['required-first-point-target-time']())

        return
      } else if (firstPoint.type === 'START' && firstPoint.target_time_departure === null) {
        this.$set(
          this.formErrors,
          'points.0.target_time_departure',
          errorInputMsg['target_time_departure/required']()
        )

        this.$mcToast.error(errorMsg['required-first-point-target-time']())

        return
      }

      if (!this.showCalculateScheduleConfirmation) {
        this.showCalculateScheduleProgressDialog()
        return
      }

      this.$bvModal.show('calculate-schedule-dialog')
    },
    async optimizeRoute() {
      try {
        this.$store.dispatch('fleetTaskInstant/setIsInitial', false)
        this.progressBarApiLoading = true
        this.optimizeRouteResponse = await FleetTaskInstant.optimize(this.$route.params.id)
      } finally {
        this.progressBarApiLoading = false
        this.refreshCheckbox += 1
      }
    },
    async calculateSchedule() {
      try {
        this.$store.dispatch('fleetTaskInstant/setIsInitial', false)
        this.progressBarApiLoading = true
        this.calculateScheduleResponse = await FleetTaskInstant.calculateSchedule(
          this.$route.params.id
        )
      } finally {
        this.progressBarApiLoading = false
      }
    },
    setupProgressBarLoading(onProgressBarFinished) {
      this.progressBarPercentage = 0
      const interval = setInterval(() => {
        // Fake loading until 80% to give time for API in case it's slow
        if (this.progressBarPercentage < 80) {
          this.progressBarPercentage += 1
        } else {
          clearInterval(interval)

          // Consistently checking whether optimize API is complete
          const finalizeInterval = setInterval(() => {
            this.finalizeProgressBarLoading(onProgressBarFinished)

            if (!this.progressBarApiLoading) {
              clearInterval(finalizeInterval)
            }
          }, 100)
        }
      }, this.FAKE_LOADING_SMOOTHNESS)
    },
    finalizeProgressBarLoading(onProgressBarFinished) {
      // If the loading is finished, continue remaining progress until 100%
      if (this.progressBarApiLoading) return

      const finalInterval = setInterval(() => {
        if (this.progressBarPercentage < 100) {
          this.progressBarPercentage += 1
        } else {
          clearInterval(finalInterval)

          onProgressBarFinished()
        }
      }, this.FAKE_LOADING_SMOOTHNESS)
    },
    optimizeProgressModalOpened() {
      this.optimizeRoute()

      const self = this
      this.setupProgressBarLoading(() => {
        self.$bvModal.hide('optimize-progress')
        if (self.optimizeRouteResponse) {
          let optimizedDistanceMetre = self.optimizeRouteResponse.data.efficiency_distance

          if (optimizedDistanceMetre >= 1000) {
            const optimizedDistanceKm = optimizedDistanceMetre / 1000
            self.$mcToast.success(successMsg['optimized-route-more-than-one-mile'](optimizedDistanceKm.toFixed(1))
            )
          } else {
            self.$mcToast.success(successMsg['optimized-route-default']())
          }

          self.$store.dispatch('fleetTaskInstant/setLoadingStartTime', Date.now())
          self.getPointsData()
          EventBus.$emit('refetch-fleet-task-instant')
        } else {
          self.$mcToast.error(errorMsg['default']())
        }

        self.optimizeRouteResponse = null
      })
    },
    calculateScheduleProgressModalOpened() {
      this.calculateSchedule()

      const self = this
      this.setupProgressBarLoading(() => {
        self.$bvModal.hide('calculate-schedule-progress')
        if (self.calculateScheduleResponse) {
          self.$mcToast.success(successMsg['calculate-schedule-default']())
          self.$store.dispatch('fleetTaskInstant/setLoadingStartTime', Date.now())
          self.getPointsData()
        } else {
          self.$mcToast.error(errorMsg['default']())
        }
        self.calculateScheduleResponse = null
      })
    },

    async handleChangeInput(event) {
      await this.$store.dispatch('fleetTaskInstant/setIsLoadingGlobalDelay', true)

      const res = await FleetTaskInstant.update(this.$route.params.id, {
        send_late_notification: event,
      })

      if (res.errors) {
        this.$mcToast.error(errorMsg['default']())
        this.$set(this.formValues, 'send_late_notification', !event)
        await this.$store.dispatch('fleetTaskInstant/setIsLoadingGlobalDelay', false)
        return
      }

      sessionStorage.setItem(
        'quickAssignmentLateNotification', JSON.stringify(event))
      await this.$store.dispatch('fleetTaskInstant/setIsLoadingGlobalDelay', false)
    }
  },
}
</script>
<style lang="scss">
@import './style.scss';
.icon-fill_home:before,
.icon-fill_flag:before {
  color: $white;
}

.progress-modal {
  .modal-dialog {
    max-width: 322px !important;
    width: 322px !important;
  }

  .caption-heading {
    font-size: 16px;
  }

  .caption {
    font-size: 13px;
  }
}

.progress {
  transition: width 0.5s ease-in-out;
}

.point-form-error {
  list-style: none;
  color: $red-400;
  font-size: 0.7109375rem;
  font-style: italic;
  font-weight: 600;
}

.point-form-error-input {
  .multiselect__tags {
    border-left-color: $red-400 !important;
    border-right-color: $red-400 !important;
    border-top-color: $red-400 !important;
    border-bottom-color: $red-400 !important;
  }
}

.trip-wrapper {
  display: flex;
  gap: 6px;
  margin-bottom: 10px;

  .point-info-wrapper {
    display: flex;
    gap: 6px;
    align-items: center;

    font-size: 12px;
    font-weight: 400;
    color: $mep-color-text-400;

    .__divider {
      height: 4px;
      width: 4px;
      background-color: $mep-primary-200;
      border-radius: 4px;
    }

    span span {
      font-weight: 500;
    }
  }

  .late-notification-wrapper {
    display: flex;
    gap: 6px;

    font-size: 12px;
    font-weight: 500;
    color: $mep-color-text-400;
  }

  .point-info-wrapper + .late-notification-wrapper {
    border-left: 1px solid $teal-100;
  }
}

</style>
